import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { fetchFlowModels } from '../../../services/api';
import { redeemPayloadToken } from '../../../services/api/payloadToken';
import { DEFAULT_FLOW_MODEL } from '../defaultFlowModel';
import { useReferenceData } from './useReferenceData';
import { useMode2Generate } from './useMode2Generate';
import { withDefaultAnswers } from '../utils/flowDefaults';
import { useAppContext } from '../../../contexts/AppContext';
import { useGenContext } from '../../../contexts/GenContext';
import type { FlowModel, FlowAnswers, FlowQuestion } from '../../../types';

/**
 * QuickGen 流程控制 hook：
 * 管理步驟切換、FlowModel 載入、問卷狀態、生成觸發、navigation guard 等邏輯。
 * index.tsx 只需負責渲染。
 */
export function useQuickGenFlow() {
  const { globalProfile, organizationName, organizationType, name, title, isAuthenticated } = useAppContext();
  const {
    genStep, setGenStep, genConfig, setGenConfig,
    generatedResult, setNavigationGuard,
    appliedToolkit, returnToToolkitList,
    restoreRequest,
  } = useGenContext();

  // ── FlowModel ──────────────────────────────────────────────────────────────
  const [allFlowModels, setAllFlowModels] = useState<FlowModel[]>([]);
  useEffect(() => {
    fetchFlowModels().then(setAllFlowModels).catch(console.error);
  }, []);

  const flowModel = useMemo<FlowModel>(() => {
    if (genConfig.flowModelId) {
      const found = allFlowModels.find(m => m.id === genConfig.flowModelId);
      if (found) return found;
    }
    return DEFAULT_FLOW_MODEL;
  }, [allFlowModels, genConfig.flowModelId]);

  // ── Flow 問卷狀態 ──────────────────────────────────────────────────────────
  const [flowPageIndex, setFlowPageIndex] = useState(0);
  // 只帶入 genConfig 既有的答案（使用者先前的作答）。
  // 不預先填任何題目 id —— 初始值由 flowModel 載入後的 defaultValue effect 決定，
  // 這樣完全自訂題目的工具包也不會被塞進不屬於它的答案。
  const [flowAnswers, setFlowAnswers] = useState<FlowAnswers>({
    ...genConfig.answers,
    imageUpload: genConfig.uploadedAssets || [],
  });

  const { ratios, styles, tones } = useReferenceData(['ratios', 'styles', 'tones']);

  // ── 外部系統 deep link：?payloadToken=&flowModelId= → 即刻切 FlowModel → redeem → 填表 ──
  // 用 lazy initial state 在 mount 第一刻就決定模式，避免「先分頁渲染、redeem 完才合併」的閃爍
  const redeemedRef = useRef(false);
  const hasDeeplinkParamOnMount = (() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).has('payloadToken');
  })();
  // AI redeem 期間的等待狀態：popup 顯示與否
  const [isRedeemingPayload, setIsRedeemingPayload] = useState(hasDeeplinkParamOnMount);
  // deeplink 模式：把所有題目集中到單一頁渲染。第一次 render 就為 true，使用者看到的就是合併後的頁面
  const [isDeeplinkMode] = useState(hasDeeplinkParamOnMount);

  useEffect(() => {
    if (redeemedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('payloadToken');
    const urlFlowModelId = params.get('flowModelId');
    if (!token) return;

    // 未登入時不 redeem（SSO-only：使用者需先回主網域登入，登入後回到此 deep link 再處理）
    if (!isAuthenticated) return;

    redeemedRef.current = true;

    // 立刻先切換 FlowModel（不等 redeem 回應）讓表單 skeleton 馬上正確
    if (urlFlowModelId) {
      setGenConfig(prev =>
        prev.flowModelId === urlFlowModelId ? prev : { ...prev, flowModelId: urlFlowModelId, answers: {} }
      );
    }

    (async () => {
      try {
        // redeem 時 B 後端才會用 Gemini 把 rawData 配對成 answers
        const { flowModelId, answers } = await redeemPayloadToken(token);
        // 以伺服器回應為準（萬一 URL 被竄改）
        setGenConfig(prev =>
          prev.flowModelId === flowModelId
            ? { ...prev, answers: { ...prev.answers, ...answers } }
            : { ...prev, flowModelId, answers }
        );
        setFlowAnswers(prev => ({ ...prev, ...answers }));
        console.log('[QuickGen] 已從外部系統匯入資料', { flowModelId, keys: Object.keys(answers) });
      } catch (err) {
        console.warn('[QuickGen] payloadToken redeem 失敗：', err);
        // 失敗不擋使用者，仍可正常手動操作 QuickGen
      } finally {
        setIsRedeemingPayload(false);
        // 清掉 URL query，避免 reload 時重複 redeem 失敗
        const cleanUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, '', cleanUrl);
      }
    })();
  }, [isAuthenticated, setGenConfig]);

  // 依 page 分組問題；deeplink 模式下把所有題目合到一頁，讓使用者一次看到 AI 填好的所有欄位
  const flowPages = useMemo<FlowQuestion[][]>(() => {
    if (isDeeplinkMode) {
      const all = [...flowModel.questions].sort((a, b) => (a.page ?? 1) - (b.page ?? 1));
      return all.length > 0 ? [all] : [];
    }
    const pageMap = new Map<number, FlowQuestion[]>();
    for (const q of flowModel.questions) {
      const p = q.page ?? 1;
      if (!pageMap.has(p)) pageMap.set(p, []);
      pageMap.get(p)!.push(q);
    }
    return [...pageMap.entries()].sort(([a], [b]) => a - b).map(([, qs]) => qs);
  }, [flowModel, isDeeplinkMode]);

  // 套用 defaultValue：flowModel 切換時補入該工具包宣告的預設值。
  // 這是初始值的唯一來源，沒有宣告 defaultValue 的題目就沒有預設值。
  useEffect(() => {
    setFlowAnswers(prev => withDefaultAnswers(prev, flowModel.questions));
  }, [flowModel]);

  // ── Mode 2 生成 ────────────────────────────────────────────────────────────
  const mode2 = useMode2Generate({ name, title, organizationName, organizationType, genConfig, setGenConfig, flowModel, restoreRequest });

  // 生成完成自動跳轉 Step 4
  useEffect(() => {
    if (generatedResult || mode2.mode2Result) setGenStep(4);
  }, [generatedResult, mode2.mode2Result, setGenStep]);

  // 在 Step 4 或生成中期間攔截 sidebar 的頁面切換
  useEffect(() => {
    if (genStep === 4 || mode2.isMode2Generating) {
      setNavigationGuard(() => window.confirm('離開後生成結果將無法取得，確定要離開嗎？'));
    } else {
      setNavigationGuard(null);
    }
    return () => setNavigationGuard(null);
  }, [genStep, mode2.isMode2Generating, setNavigationGuard]);

  // 生成中期間防止頁面重新整理或關閉
  useEffect(() => {
    if (!mode2.isMode2Generating) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [mode2.isMode2Generating]);

  // ── FloatingAssistant 事件監聯 ─────────────────────────────────────────────
  // flowAnswers 透過 ref 暴露給 listener，確保 trigger-generate 拿到最新值
  // 而不是 useEffect 註冊當下的 stale closure。flowPages.length 同理 —
  // 套用不同 toolkit 後 page 數會變，trigger 時要拿最新。
  const flowAnswersRef = useRef(flowAnswers);
  useEffect(() => { flowAnswersRef.current = flowAnswers; }, [flowAnswers]);
  const flowPagesLengthRef = useRef(flowPages.length);
  useEffect(() => { flowPagesLengthRef.current = flowPages.length; }, [flowPages.length]);

  useEffect(() => {
    /**
     * 把 partial answers 同步寫入兩處 state：
     *   - flowAnswers：form 顯示的本地工作副本
     *   - genConfig.answers：handleGenerate / Mode 2 同步前後讀取的全域副本
     * 不同步會導致畫面 / 生成讀到不一致的資料。
     */
    const syncAnswers = (patch: Record<string, unknown>) => {
      setFlowAnswers(prev => ({ ...prev, ...patch } as FlowAnswers));
      setGenConfig(prev => ({ ...prev, answers: { ...prev.answers, ...patch } as any }));
    };

    const handleSetStep = (e: Event) => {
      const step = (e as CustomEvent<{ step: number }>).detail.step;
      if (step === 4) { setGenStep(4); return; }
      if (step >= 1 && step <= 3) {
        setFlowPageIndex(step - 1);
        if (genStep === 4) setGenStep(1);
      }
    };
    /**
     * 通用單題填答 listener。AI 透過 set_quick_gen_answer 工具發送，
     * questionId 來自 get_active_toolkit_questions 回傳的題目 id。
     * 取代過去硬編特定欄位（fill-content / select-tone / update-config）
     * 的設計，讓 agent 能處理管理員自訂的工具包題目結構。
     */
    const handleSetAnswer = (e: Event) => {
      const { questionId, value } = (e as CustomEvent<{ questionId: string; value: string | boolean }>).detail;
      if (typeof questionId !== 'string' || !questionId) return;
      syncAnswers({ [questionId]: value });
    };
    // trigger-generate 等同於使用者「一路按下一步到最後、再按下生成」：
    //   1. 若還在 Step 4 結果頁，先退回 Step 1
    //   2. 把 flowPageIndex 推到 flowPages.length，讓 GenerateTriggerPanel 出現
    //   3. 等 React render 完 trigger panel，再呼叫該 panel 的 onGenerate
    //      (= mode2.handleSubmitFlow)
    // 不直接 inline 呼叫 mode2.handleSubmitFlow，避免使用者看不到自己其實正在
    // 哪個畫面被觸發生成。
    const handleTriggerGenerate = () => {
      if (genStep === 4) setGenStep(1);
      setFlowPageIndex(flowPagesLengthRef.current);
      // 用 microtask 等 React 把 trigger panel render 出來再啟動生成
      Promise.resolve().then(() => {
        mode2.handleSubmitFlow(flowAnswersRef.current);
      });
    };

    window.addEventListener('assistant:quick-gen:set-step', handleSetStep);
    window.addEventListener('assistant:quick-gen:set-answer', handleSetAnswer);
    window.addEventListener('assistant:quick-gen:trigger-generate', handleTriggerGenerate);
    return () => {
      window.removeEventListener('assistant:quick-gen:set-step', handleSetStep);
      window.removeEventListener('assistant:quick-gen:set-answer', handleSetAnswer);
      window.removeEventListener('assistant:quick-gen:trigger-generate', handleTriggerGenerate);
    };
  }, [setGenStep, setGenConfig, genStep, mode2.handleSubmitFlow]);

  // ── 回呼函式 ──────────────────────────────────────────────────────────────
  const handleAnswerChange = useCallback((id: string, value: string | boolean | File[]) => {
    setFlowAnswers(prev => ({ ...prev, [id]: value }));
  }, []);

  const handleBackFromResult = useCallback(() => {
    mode2.resetMode2();
    setGenStep(1);
    setFlowPageIndex(flowPages.length);
  }, [mode2, setGenStep, flowPages.length]);

  const handleReturnToList = useCallback(() => {
    let message: string | null = null;
    if (mode2.isMode2Generating) {
      message = '目前正在生成中，確定要返回工具包列表嗎？';
    } else if (genStep === 4) {
      message = '返回後生成結果將無法保留，確定要返回工具包列表嗎？';
    } else if (flowPageIndex > 0) {
      message = '已填寫的內容將會清除，確定要返回工具包列表嗎？';
    }
    if (message && !window.confirm(message)) return;
    returnToToolkitList();
  }, [mode2.isMode2Generating, genStep, flowPageIndex, returnToToolkitList]);

  // 進度百分比
  const totalPages = flowPages.length + 1;
  const progressPercent = genStep === 4 ? 100 : Math.round(((flowPageIndex + 1) / totalPages) * 100);

  return {
    // 來自 context 的值
    globalProfile,
    organizationName,
    organizationType,
    name,
    title,
    genStep,
    genConfig,
    generatedResult,
    appliedToolkit,

    // FlowModel
    flowModel,
    flowPages,
    flowPageIndex,
    setFlowPageIndex,
    flowAnswers,
    handleAnswerChange,

    // 參考資料
    ratios,
    styles,
    tones,

    // Mode 2 生成
    ...mode2,

    // 流程控制
    handleBackFromResult,
    handleReturnToList,
    progressPercent,

    // 外部系統 deep link 狀態
    isRedeemingPayload,
    isDeeplinkMode,
  };
}
